import { useCallback, useRef, type Dispatch, type RefObject } from "react";
import { ProvetDialogStep, Reason } from "../types";
import { scrollIntoView } from "../utilities/scrollUtilities";
import {
  ProvetBookingFlowAction,
  ProvetBookingFlowActionType,
  ProvetBookingFlowState
} from "./provetBookingFlowReducer";

interface UseBookingModalActionsOptions {
  bookingFlowState: ProvetBookingFlowState;
  bookingFlowDispatch: Dispatch<ProvetBookingFlowAction>;
  modals: {
    treatment: {
      open: (reason: Reason) => void;
      close: () => void;
      pendingReason?: Reason;
    };
    summary: {
      open: () => void;
      close: () => void;
    };
    confirmClose: {
      close: () => void;
    };
  };
  scrollRefs: {
    formRef: RefObject<HTMLFormElement>;
  };
  setReason: (reason?: Reason, transition?: boolean) => Promise<void>;
  updateBookingStep: (
    provetDialogStep: ProvetDialogStep,
    isComplete?: boolean
  ) => Promise<void>;
  /** Clears the error of a failed submission (when it may be retried) as the summary closes */
  clearSubmissionError?: () => void;
}

interface BookingModalActionsHandlers {
  /**
   * Opens the treatment's instructions modal, or selects the treatment straight away when
   * the modal would have nothing to say (no instructions and no species warning).
   */
  openReasonInstructionsModal: (reason: Reason) => Promise<void>;
  closeReasonInstructionsModal: () => Promise<void>;
  onReasonSelected: () => Promise<void>;
  closeSummaryModal: () => Promise<void>;
  openSummaryModal: () => void;
}

/**
 * Hook for managing booking modal interactions.
 * Handles treatment instructions modal and summary modal actions.
 */
export const useBookingModalActions = ({
  bookingFlowState,
  bookingFlowDispatch,
  modals,
  scrollRefs,
  setReason,
  updateBookingStep,
  clearSubmissionError
}: UseBookingModalActionsOptions): BookingModalActionsHandlers => {
  // One guard for both ways of selecting a treatment (straight from the list, or with "I
  // understand" in the pop-up): a second click, or a click on another treatment, is ignored
  // while one is being selected. The list is also locked meanwhile, see ProvetTreatmentFinder.
  const isSelectingReasonRef = useRef(false);

  const selectReason = useCallback(
    async (reason: Reason) => {
      if (isSelectingReasonRef.current) {
        return;
      }

      isSelectingReasonRef.current = true;
      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetLoadingAvailableAppointmentTimes,
        loading: true
      });

      try {
        await setReason(reason);
      } finally {
        isSelectingReasonRef.current = false;
        bookingFlowDispatch({
          type: ProvetBookingFlowActionType.SetLoadingAvailableAppointmentTimes,
          loading: false
        });
      }
    },
    [bookingFlowDispatch, setReason]
  );

  const openReasonInstructionsModal = useCallback(
    async (reason: Reason) => {
      // Check if we need to show species incompatibility warning
      const currentContactDetails = bookingFlowState.formData?.contactDetails;
      const currentPatientSpeciesCode = currentContactDetails?.patient?.species?.code;

      let showWarning = false;

      // Only check compatibility if contact details exist with a species selected,
      // and the new treatment has species restrictions
      if (
        currentContactDetails &&
        currentPatientSpeciesCode &&
        reason?.species_codes &&
        reason.species_codes.length > 0
      ) {
        // Check if current species is compatible with new treatment
        const isSpeciesCompatible = reason.species_codes.includes(
          currentPatientSpeciesCode
        );

        if (!isSpeciesCompatible) {
          showWarning = true;
        }
      }

      // Set the warning flag before opening the modal
      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetSpeciesIncompatibleWarning,
        show: showWarning
      });

      // With no instructions and no warning, the modal would only show a title and buttons:
      // select the treatment directly instead (D8)
      if (!showWarning && !reason.instructions?.trim()) {
        await selectReason(reason);
        return;
      }

      if (isSelectingReasonRef.current) {
        return; // Another treatment is being selected
      }

      modals.treatment.open(reason);
    },
    [bookingFlowState.formData, bookingFlowDispatch, modals.treatment, selectReason]
  );

  const closeReasonInstructionsModal = useCallback(async () => {
    // Reset the species incompatibility warning when closing the modal
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetSpeciesIncompatibleWarning,
      show: false
    });
    modals.treatment.close();
    await updateBookingStep(ProvetDialogStep.SelectTreatment, false);
  }, [bookingFlowDispatch, modals.treatment, updateBookingStep]);

  const onReasonSelected = useCallback(async () => {
    if (isSelectingReasonRef.current) {
      return; // Prevent multiple clicks
    }

    if (modals.treatment.pendingReason) {
      await selectReason(modals.treatment.pendingReason);
    }

    // Reset the species incompatibility warning after confirming
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetSpeciesIncompatibleWarning,
      show: false
    });

    modals.treatment.close();
  }, [modals.treatment, selectReason, bookingFlowDispatch]);

  const openSummaryModal = () => {
    modals.summary.open();

    // Scroll to form when summary modal is shown
    if (scrollRefs.formRef.current) {
      scrollIntoView(scrollRefs.formRef.current);
    }
  };

  // Back from the summary to the contact details ("Change")
  const closeSummaryModal = useCallback(async () => {
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetCompletedSteps,
      completedSteps: {
        selectContactDetails: false
      }
    });
    await updateBookingStep(ProvetDialogStep.ContactDetails, false);
    modals.summary.close();
    modals.confirmClose.close();
    // An old error would otherwise keep Confirm disabled when the summary opens again
    clearSubmissionError?.();
  }, [
    bookingFlowDispatch,
    updateBookingStep,
    modals.summary,
    modals.confirmClose,
    clearSubmissionError
  ]);

  return {
    openReasonInstructionsModal,
    closeReasonInstructionsModal,
    onReasonSelected,
    closeSummaryModal,
    openSummaryModal
  };
};
